import React, { useState, useCallback, useEffect, useRef } from "react";
import "./DownloadData.css";
import type { DataType } from "./types";
import { DataContextSection } from "./components/DataContextSection";
import { DataBreakdownSection } from "./components/DataBreakdownSection";
import { OutputConfigSection } from "./components/OutputConfigSection";
import { DownloadButton } from "./components/DownloadButton";
import MainPageContainer from "../../../components/MainPageContainer";
import {
  fetchGridcoDownloadFilters,
  startGridcoDownload,
  pollGridcoDownloadStatus,
  cancelGridcoDownload,
} from "../../../Rest_api/RestApi";
import {
  buildDataTypeConfig,
  dataTypeConfig as fallbackConfig,
} from "./dataTypeConfig";
import type { DataTypeConfig } from "./dataTypeConfig";

// ─── Page-level types ─────────────────────────────────────────────────────────

export type TimeGranularity =
  | "time_slot"
  | "hourly"
  | "daily"
  | "monthly"
  | "yearly"
  | "summary";

export type FileFormat = "excel" | "csv";

export interface DateRange {
  startDate: string;
  endDate: string;
}

export type DownloadPhase =
  | "idle"
  | "submitting"
  | "polling"
  | "success"
  | "error";

// ─── Data Type dropdown options ───────────────────────────────────────────────

export const DATA_TYPE_OPTIONS: { value: DataType; label: string }[] = [
  { value: "Demand", label: "Demand" },
  { value: "Generation", label: "Generation" },
];

const POLL_INTERVAL_MS = 3000;

// ─── Root component ───────────────────────────────────────────────────────────

export default function DownloadData() {
  const [config, setConfig] =
    useState<Record<string, DataTypeConfig>>(fallbackConfig);
  const [filtersLoading, setFiltersLoading] = useState(true);

  // Section 1 — Data Context
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: "2025-10-01",
    endDate: "2025-10-31",
  });

  useEffect(() => {
    setFiltersLoading(true);
    fetchGridcoDownloadFilters(dateRange.startDate, dateRange.endDate)
      .then((filters) => setConfig(buildDataTypeConfig(filters)))
      .catch(console.error)
      .finally(() => setFiltersLoading(false));
  }, [dateRange.startDate, dateRange.endDate]);

  const [dataType, setDataTypeRaw] = useState<DataType>("Demand");
  const [timeGranularity, setTimeGranularity] =
    useState<TimeGranularity>("time_slot");

  // Section 2 — Demand
  const [groupBy, setGroupBy] = useState<string>("total");
  const [groupBySelector, setGroupBySelector] = useState<string[]>([]);
  const [demandVariableType, setDemandVariableType] = useState<string[]>([
    "drawal",
  ]);
  const [unit, setUnit] = useState<string>("MW");

  // Section 2 — Generation
  const [generatorType, setGeneratorType] = useState<string[]>([]);
  const [generatorOwnership, setGeneratorOwnership] = useState<string[]>([]);
  const [selectedGenerators, setSelectedGenerators] = useState<string[]>([]);
  const [generationVariableType, setGenerationVariableType] = useState<
    string[]
  >(["Entitlement"]);

  // Section 3 — Output Config
  const [fileFormat, setFileFormat] = useState<FileFormat>("excel");

  // Download state
  const [downloadPhase, setDownloadPhase] = useState<DownloadPhase>("idle");
  const [statusMessage, setStatusMessage] = useState<string>("");
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jobIdRef = useRef<string | null>(null);

  const setDataType = useCallback((newType: DataType) => {
    setDataTypeRaw(newType);
    if (newType === "Demand") {
      setGroupBy("total");
      setGroupBySelector([]);
      setDemandVariableType(["drawal"]);
    } else {
      setGeneratorType([]);
      setGeneratorOwnership([]);
      setSelectedGenerators([]);
      setGenerationVariableType(["Entitlement"]);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  const buildFilename = useCallback(
    (format: string) => {
      const ext = format === "excel" ? "xlsx" : "csv";
      const start = dateRange.startDate;
      const end = dateRange.endDate;
      if (dataType === "Demand") {
        const gb = groupBy || "total";
        return `gridco_demand_${gb}_${start}_to_${end}.${ext}`;
      } else {
        const types = generatorType.length ? generatorType.join("-") : "all";
        return `gridco_generation_${types}_${start}_to_${end}.${ext}`;
      }
    },
    [dataType, dateRange, groupBy, generatorType],
  );

  const triggerFileDownload = (url: string, filename: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const poll = useCallback(
    async (jobId: string, format: string) => {
      try {
        const result = await pollGridcoDownloadStatus(jobId);

        if (result.status === "COMPLETED") {
          stopPolling();
          setDownloadPhase("success");
          setStatusMessage("Your file is ready — downloading…");
          if (result.link) {
            triggerFileDownload(result.link, buildFilename(format));
          }
          setTimeout(() => {
            setDownloadPhase("idle");
            setStatusMessage("");
          }, 4000);
        } else if (result.status === "FAILED") {
          stopPolling();
          setDownloadPhase("error");
          setStatusMessage(
            result.message || "Download failed. Please try again.",
          );
        } else {
          const label =
            result.status === "PROCESSING"
              ? "Processing your data…"
              : "Waiting in queue…";
          setStatusMessage(label);
          pollTimerRef.current = setTimeout(
            () => poll(jobId, format),
            POLL_INTERVAL_MS,
          );
        }
      } catch (err: any) {
        stopPolling();
        setDownloadPhase("error");
        setStatusMessage(err?.message || "Failed to check download status.");
      }
    },
    [stopPolling, buildFilename],
  );

  const handleDownload = useCallback(async () => {
    if (downloadPhase !== "idle") return;

    setDownloadPhase("submitting");
    setStatusMessage("Submitting request…");
    stopPolling();

    const payload =
      dataType === "Demand"
        ? {
            dateRange,
            dataType: "Demand" as const,
            timeGranularity,
            fileFormat,
            groupBy,
            groupBySelector,
            demandVariableType,
            unit,
          }
        : {
            dateRange,
            dataType: "Generation" as const,
            timeGranularity,
            fileFormat,
            generatorTypes: generatorType,
            generatorOwnerships: generatorOwnership,
            generators: selectedGenerators,
            generationVariableType,
            unit,
          };

    try {
      const { job_id } = await startGridcoDownload(payload);
      jobIdRef.current = job_id;
      setDownloadPhase("polling");
      setStatusMessage("Processing your data…");
      pollTimerRef.current = setTimeout(
        () => poll(job_id, fileFormat),
        POLL_INTERVAL_MS,
      );
    } catch (err: any) {
      setDownloadPhase("error");
      setStatusMessage(
        err?.message || "Failed to start download. Please try again.",
      );
    }
  }, [
    downloadPhase,
    dataType,
    dateRange,
    timeGranularity,
    fileFormat,
    groupBy,
    groupBySelector,
    demandVariableType,
    generatorType,
    generatorOwnership,
    selectedGenerators,
    generationVariableType,
    unit,
    poll,
    stopPolling,
  ]);

  const handleCancel = useCallback(async () => {
    const jobId = jobIdRef.current;
    stopPolling();
    setDownloadPhase("idle");
    setStatusMessage("");
    if (jobId) {
      try {
        await cancelGridcoDownload(jobId);
      } catch {
        // best-effort cancel
      }
      jobIdRef.current = null;
    }
  }, [stopPolling]);

  const handleDismissError = () => {
    setDownloadPhase("idle");
    setStatusMessage("");
  };

  return (
    <MainPageContainer className="h-fit !w-auto">
      <div
        style={{ width: "calc(100vw - 6rem)", height: "fit-content" }}
        className="gas-chart-container !w-auto"
      >
        <div className="pb-5 border-b">
          <h4 className="text-lg font-medium text-[#525062]">
            Download Energy Dataset
          </h4>
        </div>

        <div className="pt-5 flex flex-col gap-6">
          {/* 1. Data Context */}
          <DataContextSection
            dateRange={dateRange}
            setDateRange={setDateRange}
            dataType={dataType}
            setDataType={setDataType}
            timeGranularity={timeGranularity}
            setTimeGranularity={setTimeGranularity}
          />

          {/* 2. Data Breakdown */}
          <DataBreakdownSection
            dataType={dataType}
            config={config[dataType]}
            filtersLoading={filtersLoading}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            groupBySelector={groupBySelector}
            setGroupBySelector={setGroupBySelector}
            demandVariableType={demandVariableType}
            setDemandVariableType={setDemandVariableType}
            generatorType={generatorType}
            setGeneratorType={setGeneratorType}
            generatorOwnership={generatorOwnership}
            setGeneratorOwnership={setGeneratorOwnership}
            selectedGenerators={selectedGenerators}
            setSelectedGenerators={setSelectedGenerators}
            generationVariableType={generationVariableType}
            setGenerationVariableType={setGenerationVariableType}
            unit={unit}
            setUnit={setUnit}
          />

          {/* 3. Output Config */}
          <OutputConfigSection
            fileFormat={fileFormat}
            setFileFormat={setFileFormat}
          />

          {/* Download CTA */}
          <DownloadButton
            phase={downloadPhase}
            statusMessage={statusMessage}
            onDownload={handleDownload}
            onDismissError={handleDismissError}
            onCancel={handleCancel}
          />
        </div>
      </div>
    </MainPageContainer>
  );
}
